// View Current Stock — legacy /Reports/ItemStockReport (menu MOID 294, "View Item Stock").
// Flow: pick CC (or Select All) + category → View. With CC = Select All a specific category is required and the
// item code must be searched (spGetViewItemsDetail only filters by item when @CCCode = 'SelectAll').
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { Warehouse, ChevronRight, Download, Loader2, AlertTriangle, Filter, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

import {
    fetchStockCostCenterCodes, fetchStockCategories, fetchItemCodeSuggestions, fetchViewItemsGrid,
    clearStockRows, resetViewCurrentStock,
    selectStockCostCenters, selectStockCategories, selectStockRows, selectStockLoaded,
    selectStockLoading, selectStockErrors,
} from '../../slices/stockSlice/viewCurrentStockSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-60';

const SELECT_ALL_CC = 'SelectAll';
const SELECT_ALL_CAT = '0';

const toNum = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const fmtAmt = (v) => toNum(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (v) => toNum(v).toLocaleString('en-IN', { maximumFractionDigits: 3 });
// masterdata item codes carry leading spaces (" 3OC00159")
const txt = (v) => String(v ?? '').trim();

// Legacy grid column order (ViewItemsGrid.cshtml) — also the Excel column order (CCStockToDataTable).
const COLUMNS = [
    { key: 'Costcenter',    label: 'CC Code' },
    { key: 'Itemcode',      label: 'Item Code' },
    { key: 'ItemName',      label: 'Item Name' },
    { key: 'Specification', label: 'Specification' },
    { key: 'DCA',           label: 'DCA Code' },
    { key: 'SubDca',        label: 'SDCA Code' },
    { key: 'Basic',         label: 'Purchase Price', num: true, fmt: fmtAmt },
    { key: 'Units',         label: 'Units' },
    { key: 'Qty',           label: 'Qty', num: true, fmt: fmtQty },
    { key: 'TotalValue',    label: 'Total Value', num: true, fmt: fmtAmt },
];

const ViewCurrentStockPage = () => {
    const dispatch = useDispatch();
    const costCenters = useSelector(selectStockCostCenters);
    const categories = useSelector(selectStockCategories);
    const rows = useSelector(selectStockRows);
    const stockLoaded = useSelector(selectStockLoaded);
    const loading = useSelector(selectStockLoading);
    const errors = useSelector(selectStockErrors);

    const { userData } = useSelector((state) => state.auth);
    const uid = userData?.UID || userData?.uid;
    const roleId = userData?.roleId || userData?.RID;

    const [ccCode, setCcCode] = useState('');
    const [catId, setCatId] = useState('');
    const [itemText, setItemText] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [gridSearch, setGridSearch] = useState('');
    const [sort, setSort] = useState({ key: null, dir: 'asc' });
    const [ranLabels, setRanLabels] = useState({ cc: '', cat: '' });
    const suggestSeq = useRef(0);

    useEffect(() => {
        dispatch(fetchStockCategories());
        return () => { dispatch(resetViewCurrentStock()); };
    }, [dispatch]);

    useEffect(() => {
        if (uid && roleId) dispatch(fetchStockCostCenterCodes({ UID: uid, RID: roleId }));
    }, [dispatch, uid, roleId]);

    useEffect(() => {
        if (errors.costCenters) toast.error(errors.costCenters);
        if (errors.categories) toast.error(errors.categories);
    }, [errors.costCenters, errors.categories]);

    // Legacy ModeChange(): item search only for CC = Select All + a specific category.
    const showItemSearch = ccCode === SELECT_ALL_CC && catId !== '' && catId !== SELECT_ALL_CAT;

    const resetResults = () => {
        dispatch(clearStockRows());
        setValidationErrors([]);
        setGridSearch('');
    };

    const handleCcChange = (v) => { setCcCode(v); setItemText(''); setSuggestions([]); resetResults(); };
    const handleCatChange = (v) => { setCatId(v); setItemText(''); setSuggestions([]); resetResults(); };

    // Legacy autocomplete: GetAutoCompleteitemcode(prefix, category) → top 20 "CODE , NAME | SPEC".
    useEffect(() => {
        if (!showItemSearch || !itemText.trim()) { setSuggestions([]); return undefined; }
        const seq = ++suggestSeq.current;
        const t = setTimeout(() => {
            dispatch(fetchItemCodeSuggestions({ Pfx: itemText.trim(), Cat: catId }))
                .unwrap()
                .then((res) => { if (seq === suggestSeq.current) setSuggestions((res?.Data || []).map((r) => r.SearchItem).filter(Boolean)); })
                .catch(() => { if (seq === suggestSeq.current) setSuggestions([]); });
        }, 300);
        return () => clearTimeout(t);
    }, [dispatch, itemText, catId, showItemSearch]);

    const ccLabel = costCenters.find((c) => c.CCID === ccCode)?.CCVAL || '';
    const catLabel = categories.find((c) => String(c.Categoryid) === catId)?.CategoryValue || '';

    // Legacy SubmitViewItems() validations.
    const handleView = () => {
        const errs = [];
        if (!ccCode) errs.push('Select CC Code');
        if (!catId) errs.push('Select Category');
        if (ccCode === SELECT_ALL_CC && catId === SELECT_ALL_CAT) errs.push('Invalid Selection');
        if (showItemSearch && !itemText.trim()) errs.push('Enter Item Code');
        setValidationErrors(errs);
        if (errs.length) { dispatch(clearStockRows()); return; }

        // The search box holds "CODE , NAME | SPEC" — only the code goes to the SP (legacy split(',')[0]).
        const code = showItemSearch ? itemText.split(',')[0].trim() : '';
        setGridSearch('');
        setSort({ key: null, dir: 'asc' });
        setRanLabels({ cc: ccLabel, cat: catLabel });
        dispatch(fetchViewItemsGrid({ CCode: ccCode, Cat: catId, ItemText: code, UID: uid, RID: roleId }));
    };

    const visibleRows = useMemo(() => {
        const q = gridSearch.trim().toLowerCase();
        let list = q ? rows.filter((r) => COLUMNS.some((c) => txt(r[c.key]).toLowerCase().includes(q))) : rows;
        if (sort.key) {
            const col = COLUMNS.find((c) => c.key === sort.key);
            const dir = sort.dir === 'asc' ? 1 : -1;
            list = [...list].sort((a, b) => (col.num
                ? (toNum(a[sort.key]) - toNum(b[sort.key]))
                : txt(a[sort.key]).localeCompare(txt(b[sort.key]))) * dir);
        }
        return list;
    }, [rows, gridSearch, sort]);

    const totals = useMemo(() => visibleRows.reduce((acc, r) => ({
        qty: acc.qty + toNum(r.Qty), value: acc.value + toNum(r.TotalValue),
    }), { qty: 0, value: 0 }), [visibleRows]);

    const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

    // Legacy ExportExcelItemStock: same 10 columns, numeric Purchase Price / Qty / Total Value.
    const handleExport = () => {
        if (!rows.length) { toast.warning('No data to export'); return; }
        const data = rows.map((r) => {
            const o = {};
            COLUMNS.forEach((c) => { o[c.label] = c.num ? toNum(r[c.key]) : txt(r[c.key]); });
            return o;
        });
        const ws = XLSX.utils.json_to_sheet(data, { header: COLUMNS.map((c) => c.label) });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Stock');
        const safe = (s) => String(s || '').replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();
        XLSX.writeFile(wb, `Stock For ${safe(ranLabels.cc)}, For Category ${safe(ranLabels.cat)} Report.xlsx`);
    };

    const SortIcon = ({ colKey }) => {
        if (sort.key !== colKey) return <ArrowUpDown className="w-3 h-3 opacity-50" />;
        return sort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Warehouse className="h-4 w-4 text-white" />
                            </div>
                            View Current Stock
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Items currently in stock at a cost centre, or one item across all your cost centres</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">View Current Stock</span>
                        </nav>
                    </div>
                    <button onClick={handleExport} disabled={rows.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                        <Download className="h-3.5 w-3.5" />Export Excel
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-4">
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Cost Centre</label>
                        <select className={inputCls} value={ccCode} onChange={(e) => handleCcChange(e.target.value)} disabled={loading.costCenters}>
                            <option value="">{loading.costCenters ? 'Loading…' : '-Please Select-'}</option>
                            {costCenters.map((c) => <option key={c.CCID} value={c.CCID}>{c.CCVAL}</option>)}
                        </select>
                    </div>
                    <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Item Category</label>
                        <select className={inputCls} value={catId} onChange={(e) => handleCatChange(e.target.value)} disabled={loading.categories}>
                            <option value="">{loading.categories ? 'Loading…' : '-Please Select-'}</option>
                            {categories.map((c) => <option key={String(c.Categoryid)} value={String(c.Categoryid)}>{c.CategoryValue}</option>)}
                        </select>
                    </div>
                    {showItemSearch && (
                        <div className="md:col-span-4 relative">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Search Item</label>
                            <input
                                className={`${inputCls} uppercase`}
                                value={itemText}
                                placeholder="Type item code or name…"
                                onChange={(e) => { setItemText(e.target.value); setShowSuggestions(true); dispatch(clearStockRows()); }}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                            />
                            {showSuggestions && suggestions.length > 0 && (
                                <ul className="absolute z-20 mt-1 w-full max-h-60 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
                                    {suggestions.map((s) => (
                                        <li key={s}>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => { setItemText(s); setShowSuggestions(false); }}
                                                className="w-full text-left px-2.5 py-1.5 text-xs text-gray-800 dark:text-gray-100 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                                            >{s}</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    <div className={showItemSearch ? 'md:col-span-1' : 'md:col-span-2'}>
                        <button onClick={handleView} disabled={loading.stockRows} className="w-full px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60">View</button>
                    </div>
                </div>
                {validationErrors.length > 0 && (
                    <div className="mt-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-red-700 dark:text-red-300 space-y-0.5">
                        {validationErrors.map((m) => <p key={m}>{m}</p>)}
                    </div>
                )}
            </div>

            {/* Report */}
            {loading.stockRows ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading stock…</span>
                </div>
            ) : errors.stockRows ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{errors.stockRows}</span>
                </div>
            ) : stockLoaded && rows.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No Data Found</div>
            ) : rows.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">
                            Stock for {ranLabels.cc} · {ranLabels.cat}
                            <span className="ml-2 font-normal text-gray-400">({visibleRows.length}{visibleRows.length !== rows.length ? ` of ${rows.length}` : ''} items)</span>
                        </h2>
                        <div className="relative w-56">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input className={`${inputCls} pl-7`} placeholder="Search grid…" value={gridSearch} onChange={(e) => setGridSearch(e.target.value)} />
                        </div>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    {COLUMNS.map((c) => (
                                        <th key={c.key} className={`px-2 py-1.5 text-[10px] font-bold text-white uppercase whitespace-nowrap ${c.num ? 'text-right' : 'text-left'}`}>
                                            <button type="button" onClick={() => toggleSort(c.key)} className={`inline-flex items-center gap-1 ${c.num ? 'flex-row-reverse' : ''}`}>
                                                {c.label}<SortIcon colKey={c.key} />
                                            </button>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {visibleRows.map((r, i) => (
                                    <tr key={`${r.Costcenter}-${r.Itemcode}-${i}`} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        {COLUMNS.map((c) => (
                                            <td
                                                key={c.key}
                                                title={c.key === 'ItemName' || c.key === 'Specification' ? txt(r[c.key]) : undefined}
                                                className={`px-2 py-1 text-xs ${c.num ? 'text-right tabular-nums whitespace-nowrap text-gray-800 dark:text-gray-100' : 'text-gray-700 dark:text-gray-200'} ${c.key === 'ItemName' || c.key === 'Specification' ? 'max-w-[220px] truncate' : c.num ? '' : 'whitespace-nowrap'} ${c.key === 'Itemcode' ? 'font-semibold' : ''}`}
                                            >
                                                {c.fmt ? c.fmt(r[c.key]) : txt(r[c.key])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="sticky bottom-0">
                                <tr className="bg-gray-100 dark:bg-gray-900 font-bold">
                                    <td className="px-2 py-1.5 text-xs" colSpan={8}>TOTAL</td>
                                    <td className="px-2 py-1.5 text-xs text-right tabular-nums">{fmtQty(totals.qty)}</td>
                                    <td className="px-2 py-1.5 text-xs text-right tabular-nums">{fmtAmt(totals.value)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ViewCurrentStockPage;
